import { Link } from "react-router-dom";
import CategoryCard from "../components/CategoryCard.js";
import { categories } from "../data/categories.js";
import homeImage from "../assets/home/home1.webp";
import homeImage2 from "../assets/home/home2.webp";
import homeImage3 from "../assets/home/home3.webp";
import FaqSection from "../components/FaqSection.js";

const HomePage = () => (
  <main className="bg-slate-50 text-containx-navy">
    <section className="relative overflow-hidden bg-containx-navy text-white">
      <div className="absolute -right-24 -top-24 h-80 w-80 rounded-full bg-containx-tan/20 blur-3xl" />
      <div className="relative mx-auto grid max-w-7xl items-center gap-12 px-6 py-16 md:py-24 lg:grid-cols-2 lg:py-28">
        <div>
          <p className="mb-4 text-sm font-bold uppercase tracking-widest text-containx-beige">
            Restaurant packaging & supplies
          </p>
          <h1 className="max-w-2xl text-4xl font-bold leading-tight md:text-6xl">
            The supplies you need, without the runaround.
          </h1>
          <p className="mt-6 max-w-xl text-lg leading-8 text-slate-200">
            Shop containers, cups, bags, cutlery, gloves, and everyday
            food-service essentials from a responsive local supplier.
          </p>
          <div className="mt-8 flex flex-col gap-3 sm:flex-row">
            <Link
              to="/products"
              className="rounded-lg bg-containx-tan px-6 py-3 text-center font-bold text-white transition hover:bg-containx-beige hover:text-containx-navy"
            >
              Browse Products
            </Link>
            <a
              href="tel:3019698766"
              className="rounded-lg border border-white/50 px-6 py-3 text-center font-bold text-white transition hover:bg-white hover:text-containx-navy"
            >
              Call 301-969-8766
            </a>
          </div>
          <div className="mt-8 flex flex-wrap gap-x-7 gap-y-2 text-sm text-slate-200">
            <span>✓ Bulk quantities</span>
            <span>✓ Local service</span>
            <span>✓ Responsive support</span>
          </div>
        </div>
        <div
          className="grid grid-cols-2 gap-4"
          aria-label="Featured product categories"
        >
          <Link
            to="/plastic-containers"
            className="col-span-2 overflow-hidden rounded-2xl bg-white shadow-2xl"
          >
            <img
              src={homeImage}
              alt="Plastic hinged clamshell food containers"
              width="1200"
              height="609"
              fetchPriority="high"
              decoding="async"
              className="h-56 w-full object-cover transition duration-500 hover:scale-105 md:h-64"
            />
          </Link>
          <Link
            to="/bags"
            className="overflow-hidden rounded-2xl bg-white shadow-xl"
          >
            <img
              src={homeImage2}
              alt="Plastic t-shirt bags, paper brown bags with handles, and trash bags"
              width="700"
              height="933"
              decoding="async"
              className="h-36 w-full object-cover transition duration-500 hover:scale-105"
            />
          </Link>
          <Link
            to="/cups"
            className="overflow-hidden rounded-2xl bg-white shadow-xl"
          >
            <img
              src={homeImage3}
              alt="Hot and cold cups with lids and cup holder"
              width="900"
              height="675"
              decoding="async"
              className="h-36 w-full object-cover transition duration-500 hover:scale-105"
            />
          </Link>
        </div>
      </div>
    </section>

    <section className="mx-auto max-w-7xl px-6 py-14 md:py-20">
      <div className="mb-9 flex flex-col justify-between gap-4 sm:flex-row sm:items-end">
        <div>
          <p className="mb-2 text-sm font-bold uppercase tracking-widest text-containx-tan">
            Shop by category
          </p>
          <h2 className="text-3xl font-bold md:text-4xl">
            Everything your operation uses
          </h2>
        </div>
        <Link
          to="/products"
          className="font-bold text-containx-tan hover:underline"
        >
          View all products →
        </Link>
      </div>
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {categories.map((category) => (
          <CategoryCard key={category.slug} category={category} compact />
        ))}
      </div>
    </section>

    <section className="bg-white py-14 md:py-20">
      <div className="mx-auto max-w-7xl px-6">
        <div className="mx-auto max-w-3xl text-center">
          <p className="mb-2 text-sm font-bold uppercase tracking-widest text-containx-tan">
            Why ContainX
          </p>
          <h2 className="text-3xl font-bold md:text-4xl">
            Straightforward service for busy businesses
          </h2>
        </div>
        <div className="mt-10 grid gap-6 md:grid-cols-3">
          <article className="rounded-2xl border border-slate-200 p-7">
            <div className="mb-5 flex h-11 w-11 items-center justify-center rounded-full bg-containx-beige text-xl">
              ✓
            </div>
            <h3 className="text-xl font-semibold">Dependable products</h3>
            <p className="mt-3 leading-7 text-slate-600">
              Practical packaging and supplies selected for restaurants,
              caterers, food trucks, and commercial kitchens.
            </p>
          </article>
          <article className="rounded-2xl border border-slate-200 p-7">
            <div className="mb-5 flex h-11 w-11 items-center justify-center rounded-full bg-containx-beige text-xl">
              $
            </div>
            <h3 className="text-xl font-semibold">Built for bulk orders</h3>
            <p className="mt-3 leading-7 text-slate-600">
              Case quantities and wholesale-friendly ordering help keep your
              operation stocked and your costs predictable.
            </p>
          </article>
          <article className="rounded-2xl border border-slate-200 p-7">
            <div className="mb-5 flex h-11 w-11 items-center justify-center rounded-full bg-containx-beige text-xl">
              ☎
            </div>
            <h3 className="text-xl font-semibold">Real, responsive help</h3>
            <p className="mt-3 leading-7 text-slate-600">
              Call or text us directly when you need help choosing a product,
              confirming availability, or placing an order.
            </p>
          </article>
        </div>
      </div>
    </section>

    <FaqSection />

    <section className="bg-containx-beige py-14 md:py-16">
      <div className="mx-auto flex max-w-5xl flex-col items-center justify-between gap-7 px-6 text-center md:flex-row md:text-left">
        <div>
          <p className="text-sm font-bold uppercase tracking-widest">
            Ready to order?
          </p>
          <h2 className="mt-2 text-3xl font-bold">
            Tell us the SKUs and quantities you need.
          </h2>
          <p className="mt-3 max-w-2xl">
            Browse online or open the full catalog, then call or text our team
            for availability and ordering.
          </p>
        </div>
        <div className="flex w-full shrink-0 flex-col gap-3 sm:w-auto">
          <a
            href="sms:3019698766"
            className="rounded-lg bg-containx-navy px-6 py-3 text-center font-bold text-white transition hover:bg-containx-charcoal"
          >
            Text Us
          </a>
          <a
            href="/pdf/product-catalog.pdf"
            target="_blank"
            rel="noopener noreferrer"
            className="rounded-lg border-2 border-containx-navy px-6 py-3 text-center font-bold transition hover:bg-white"
          >
            View Catalog
          </a>
        </div>
      </div>
    </section>
  </main>
);

export default HomePage;
